import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';

// Global state to prevent multiple instances from running simultaneously
let globalInitializationInProgress = false;
let globalSessionMap = new Map(); // deviceId -> sessionId mapping
let globalDeviceProcessingMap = new Map(); // deviceId -> processing status

const GameSessionManager = forwardRef(({ deviceInfo, onGameComplete, onGroupUpdate }, ref) => {
  const [sessionId, setSessionId] = useState(null);
  const [groupId, setGroupId] = useState(null);
  const [playerInfo, setPlayerInfo] = useState(null);
  const [groupStatus, setGroupStatus] = useState('waiting');
  const [isInitializing, setIsInitializing] = useState(false);
  const [gameStats, setGameStats] = useState({
    startTime: null,
    endTime: null,
    tapCount: 0,
    gameTime: 0,
    won: false
  });

  const API_BASE = process.env.REACT_APP_API_URL;

  // Create game session when component mounts (only once)
  useEffect(() => {
    let mounted = true;
    
    if (!isInitializing && !sessionId && deviceInfo && deviceInfo.id && mounted) {
      // Check if this device is already being processed
      if (globalDeviceProcessingMap.has(deviceInfo.id)) {
        console.log('GameSessionManager: Device already being processed, waiting...');
        return;
      }
      
      // Check if this device already has a session
      if (globalSessionMap.has(deviceInfo.id)) {
        console.log('GameSessionManager: Device already has a session, skipping initialization');
        return;
      }
      
      // Initialize group info from deviceInfo if available
      if (deviceInfo.groupId && deviceInfo.playerInfo && !groupId) {
        console.log('GameSessionManager: Initializing group info from deviceInfo:', deviceInfo.groupId);
        setGroupId(deviceInfo.groupId);
        setPlayerInfo(deviceInfo.playerInfo);
      }
      
      console.log('GameSessionManager: Initializing game session for device:', deviceInfo.id);
      globalDeviceProcessingMap.set(deviceInfo.id, true);
      setIsInitializing(true);
      createGameSession();
    } else if (sessionId) {
      console.log('GameSessionManager: Session already exists, skipping initialization');
    } else if (!deviceInfo || !deviceInfo.id) {
      console.log('GameSessionManager: No device info, waiting...');
    }
    
    return () => {
      mounted = false;
      // Only clean up if we haven't successfully created a session
      // This prevents the second instance from proceeding after cleanup
      if (deviceInfo && deviceInfo.id && !sessionId) {
        globalSessionMap.delete(deviceInfo.id);
        globalDeviceProcessingMap.delete(deviceInfo.id);
        console.log('GameSessionManager: Cleaned up global maps for device (no session created):', deviceInfo.id);
      }
    };
  }, [isInitializing, sessionId, deviceInfo, groupId]);

  // Poll for group updates when in a group
  useEffect(() => {
    let interval;
    if (groupId && groupStatus === 'waiting') {
      interval = setInterval(() => {
        checkGroupStatus();
      }, 2000); // Check every 2 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [groupId, groupStatus]);

  // Auto-refresh group info every 5 seconds when in a group
  useEffect(() => {
    let interval;
    if (groupId) {
      interval = setInterval(() => {
        refreshGroupInfo();
      }, 5000); // Check every 5 seconds
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [groupId]);

  // Auto-join latest available group (join existing or create new if needed)
  const autoJoinLatestGroup = async () => {
    try {
      console.log('GameSessionManager: autoJoinLatestGroup called, current groupId:', groupId);
      
      // If already in a group, don't try to join another one
      if (groupId) {
        console.log('GameSessionManager: Already in a group, not joining another one');
        return { success: true, alreadyInGroup: true };
      }

      // Check if another instance is already processing this device
      if (globalDeviceProcessingMap.has(deviceInfo.id) && !isInitializing) {
        console.log('GameSessionManager: Another instance is processing this device, waiting...');
        return { success: false, error: 'Another instance is processing this device' };
      }
      
      // Double-check that we're still the active instance
      if (!globalDeviceProcessingMap.has(deviceInfo.id)) {
        console.log('GameSessionManager: No longer the active instance, aborting auto-join');
        return { success: false, error: 'No longer the active instance' };
      }

      console.log('GameSessionManager: Fetching available groups...');
      const response = await fetch(`${API_BASE}/api/groups`);
      const data = await response.json();
      
      if (data.success && data.groups.length > 0) {
        console.log(`GameSessionManager: Found ${data.groups.length} total groups`);
        
        // Find the latest waiting group that has space
        // Sort by creation date (newest first) and check available spots
        const availableGroups = data.groups
          .filter(group => 
            group.status === 'waiting' && 
            group.current_players < group.max_players
          )
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        
        console.log(`GameSessionManager: Found ${availableGroups.length} available groups`);
        
        if (availableGroups.length > 0) {
          const targetGroup = availableGroups[0]; // Latest available group
          console.log(`GameSessionManager: Joining available group: ${targetGroup.name} (${targetGroup.current_players}/${targetGroup.max_players} players)`);
          
          const joinResult = await joinGroup(targetGroup.group_id);
          if (joinResult.success) {
            console.log('GameSessionManager: Successfully auto-joined existing group:', targetGroup.name);
            return { success: true, joinedGroup: true, groupId: targetGroup.group_id };
          } else {
            console.log('GameSessionManager: Failed to join group:', joinResult.error);
          }
        }
      } else {
        console.log('GameSessionManager: No groups found in database');
      }
      
      // If no groups available or all are full, create a new one
      console.log('GameSessionManager: No available groups to join, creating new group...');
      await createNewGroup();
      return { success: true, createdNewGroup: true };
    } catch (error) {
      console.error('Failed to auto-join group:', error);
      return { success: false, error: error.message };
    }
  };

  // Create new group when QR code is scanned
  const createNewGroup = async () => {
    try {
      console.log('GameSessionManager: Creating new group...');
      
      // Double-check we're not already in a group
      if (groupId) {
        console.log('GameSessionManager: Already in a group, not creating new one');
        return;
      }
      
      // Check if we're still the instance that should be creating the group
      if (!globalDeviceProcessingMap.has(deviceInfo.id)) {
        console.log('GameSessionManager: No longer the active instance, not creating group');
        return;
      }
      
      const groupName = `Group ${Math.floor(Math.random() * 1000)}`;
      console.log('GameSessionManager: Creating group with name:', groupName);
      
      const response = await fetch(`${API_BASE}/api/groups/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: groupName,
          maxPlayers: 5
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('GameSessionManager: Successfully created new group:', groupName, 'with ID:', data.groupId);
        // Join the newly created group
        const joinResult = await joinGroup(data.groupId);
        if (joinResult.success) {
          console.log('GameSessionManager: Successfully joined newly created group');
        } else {
          console.log('GameSessionManager: Failed to join newly created group:', joinResult.error);
        }
      } else {
        console.log('GameSessionManager: Failed to create group:', data.error);
      }
    } catch (error) {
      console.error('GameSessionManager: Failed to create new group:', error);
    }
  };

  // Create group and join when QR code is scanned (called from parent)
  const createGroupAndJoin = async () => {
    try {
      // Use autoJoinLatestGroup which will join existing or create new if needed
      const result = await autoJoinLatestGroup();
      return result;
    } catch (error) {
      console.error('Failed to create group and join:', error);
      return { success: false, error: error.message };
    }
  };

  // Handle QR code scan - this should be called when a QR code is scanned
  const handleQRCodeScan = async () => {
    try {
      // Use autoJoinLatestGroup which will join existing or create new if needed
      const result = await autoJoinLatestGroup();
      if (result && result.success) {
        if (result.alreadyInGroup) {
          console.log('Already in a group, no action needed');
          return result;
        }
        if (result.joinedGroup) {
          console.log('Successfully joined existing group');
          return result;
        }
        if (result.createdNewGroup) {
          console.log('Created and joined new group');
          return result;
        }
      }
      
      return result;
    } catch (error) {
      console.error('Failed to handle QR code scan:', error);
      return { success: false, error: error.message };
    }
  };

  // Check if player is already in a group
  const isInGroup = () => {
    const inGroup = groupId !== null && playerInfo !== null;
    console.log('GameSessionManager: isInGroup check - groupId:', groupId, 'playerInfo:', playerInfo, 'result:', inGroup);
    return inGroup;
  };

  // Leave current group
  const leaveGroup = async () => {
    if (!groupId) return { success: false, error: 'Not in a group' };
    
    try {
      console.log('GameSessionManager: Leaving group:', groupId);
      
      // Reset group state
      setGroupId(null);
      setPlayerInfo(null);
      setGroupStatus('waiting');
      
      console.log('GameSessionManager: Successfully left group');
      return { success: true, message: 'Left group successfully' };
    } catch (error) {
      console.error('GameSessionManager: Failed to leave group:', error);
      return { success: false, error: error.message };
    }
  };

  // Reset session (for debugging/testing)
  const resetSession = async () => {
    console.log('GameSessionManager: Resetting session...');
    setSessionId(null);
    setGroupId(null);
    setPlayerInfo(null);
    setGroupStatus('waiting');
    setIsInitializing(false);
    
    // Remove from global maps
    if (deviceInfo && deviceInfo.id) {
      globalSessionMap.delete(deviceInfo.id);
      globalDeviceProcessingMap.delete(deviceInfo.id);
      console.log('GameSessionManager: Removed device from global maps');
    }
    
    console.log('GameSessionManager: Session reset complete');
  };

  // Check if device is already being processed globally
  const isDeviceProcessing = () => {
    return globalDeviceProcessingMap.has(deviceInfo?.id) || globalSessionMap.has(deviceInfo?.id);
  };

  // Check if this instance is the active one for the device
  const isActiveInstance = () => {
    return globalDeviceProcessingMap.has(deviceInfo?.id);
  };

  // Get global status for debugging
  const getGlobalStatus = () => {
    return {
      globalInitializationInProgress,
      globalSessionMap: Object.fromEntries(globalSessionMap),
      globalDeviceProcessingMap: Object.fromEntries(globalDeviceProcessingMap),
      currentDeviceId: deviceInfo?.id,
      isProcessing: isDeviceProcessing()
    };
  };

  // Clear all global state (for debugging/testing)
  const clearGlobalState = () => {
    globalInitializationInProgress = false;
    globalSessionMap.clear();
    globalDeviceProcessingMap.clear();
    console.log('GameSessionManager: Cleared all global state');
  };

  // Get current group status
  const getCurrentGroupStatus = () => {
    return {
      groupId,
      playerInfo,
      groupStatus,
      isInGroup: isInGroup()
    };
  };

  // Check if current group is full
  const isCurrentGroupFull = async () => {
    if (!groupId) return false;
    
    try {
      const groupData = await getGroupInfo();
      if (groupData && groupData.group) {
        return groupData.group.current_players >= groupData.group.max_players;
      }
      return false;
    } catch (error) {
      console.error('Failed to check if group is full:', error);
      return false;
    }
  };

  // Check if a specific group has available spots
  const hasGroupAvailableSpots = (group) => {
    return group.status === 'waiting' && group.current_players < group.max_players;
  };

  // Get available spots in current group
  const getCurrentGroupAvailableSpots = async () => {
    if (!groupId) return 0;
    
    try {
      const groupData = await getGroupInfo();
      if (groupData && groupData.group) {
        const available = groupData.group.max_players - groupData.group.current_players;
        return Math.max(0, available);
      }
      return 0;
    } catch (error) {
      console.error('Failed to get available spots:', error);
      return 0;
    }
  };

  // Get current group info
  const getCurrentGroupInfo = async () => {
    if (!groupId) return null;
    
    try {
      const groupData = await getGroupInfo();
      if (groupData && groupData.group) {
        return {
          group: groupData.group,
          players: groupData.players || [],
          isFull: groupData.group.current_players >= groupData.group.max_players
        };
      }
      return null;
    } catch (error) {
      console.error('Failed to get current group info:', error);
      return null;
    }
  };

  // Create new game session
  const createGameSession = async () => {
    try {
      console.log('GameSessionManager: Creating game session for device:', deviceInfo.id);
      
      // Check if we already have a session
      if (sessionId) {
        console.log('GameSessionManager: Session already exists:', sessionId);
        return;
      }
      
      // Double-check global session map
      if (globalSessionMap.has(deviceInfo.id)) {
        console.log('GameSessionManager: Device already has a session in global map, skipping');
        return;
      }
      
      const response = await fetch(`${API_BASE}/api/game/session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceId: deviceInfo.id
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('GameSessionManager: Session created successfully:', data.sessionId);
        
        // Add to global session map
        globalSessionMap.set(deviceInfo.id, data.sessionId);
        
        setSessionId(data.sessionId);
        setGameStats(prev => ({
          ...prev,
          startTime: Date.now()
        }));
        
                 // Group joining is now handled in the sign-in process
         // Update session with group ID if available
         if (groupId) {
           console.log('GameSessionManager: Updating session with group ID:', groupId);
           await updateSessionGroup(groupId);
         }
         console.log('GameSessionManager: Session created successfully, group already assigned during sign-in');
      }
    } catch (error) {
      console.error('GameSessionManager: Failed to create game session:', error);
    } finally {
      setIsInitializing(false);
      // Remove device from processing map
      if (deviceInfo && deviceInfo.id) {
        globalDeviceProcessingMap.delete(deviceInfo.id);
        console.log('GameSessionManager: Removed device from processing map');
      }
    }
  };

  // Join a game group
  const joinGroup = async (targetGroupId) => {
    try {
      console.log('GameSessionManager: joinGroup called for group:', targetGroupId, 'current groupId:', groupId);
      
      // Prevent joining if already in any group
      if (groupId) {
        console.log('GameSessionManager: Already in a group, cannot join another one');
        return { success: false, error: 'Already in a group' };
      }
      
      // Prevent joining if already in this specific group
      if (groupId === targetGroupId) {
        console.log('GameSessionManager: Already in group:', targetGroupId);
        return { success: true, playerInfo: playerInfo };
      }
      
      // Check if we're still the active instance
      if (!globalDeviceProcessingMap.has(deviceInfo.id)) {
        console.log('GameSessionManager: No longer the active instance, aborting join');
        return { success: false, error: 'No longer the active instance' };
      }
      
      console.log('GameSessionManager: Attempting to join group:', targetGroupId);
      const response = await fetch(`${API_BASE}/api/groups/${targetGroupId}/join`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceId: deviceInfo.id
        })
      });

      const data = await response.json();
      
      if (data.success) {
        console.log('GameSessionManager: Successfully joined group:', targetGroupId, 'with player info:', data);
        
        setGroupId(targetGroupId);
        setPlayerInfo({
          color: data.playerColor,
          laneNumber: data.laneNumber
        });
        
        // Update game session with group ID
        if (sessionId) {
          console.log('GameSessionManager: Updating session with group ID:', targetGroupId);
          await updateSessionGroup(targetGroupId);
        }
        
        if (onGroupUpdate) {
          console.log('GameSessionManager: Notifying parent of group update');
          onGroupUpdate({
            groupId: targetGroupId,
            playerInfo: {
              color: data.playerColor,
              laneNumber: data.laneNumber
            }
          });
        }
        
        console.log('GameSessionManager: Group join completed successfully');
        return { success: true, playerInfo: data };
      } else {
        console.log('GameSessionManager: Failed to join group:', data.error);
        return { success: false, error: data.error };
      }
    } catch (error) {
      console.error('Failed to join group:', error);
      return { success: false, error: 'Network error' };
    }
  };

  // Update session with group ID
  const updateSessionGroup = async (targetGroupId) => {
    try {
      await fetch(`${API_BASE}/api/game/session/${sessionId}/group`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          groupId: targetGroupId
        })
      });
    } catch (error) {
      console.error('Failed to update session group:', error);
    }
  };

  // Check group status
  const checkGroupStatus = async () => {
    if (!groupId) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/groups/${groupId}`);
      const data = await response.json();
      
      if (data.success) {
        const newStatus = data.group.status;
        setGroupStatus(newStatus);
        
        if (newStatus === 'active' && groupStatus === 'waiting') {
          // Game just started
          setGameStats(prev => ({
            ...prev,
            startTime: Date.now()
          }));
          
          if (onGroupUpdate) {
            onGroupUpdate({ groupId, status: 'active' });
          }
        }
      }
    } catch (error) {
      console.error('Failed to check group status:', error);
    }
  };

  // Get group information
  const getGroupInfo = async () => {
    if (!groupId) return null;
    
    try {
      const response = await fetch(`${API_BASE}/api/groups/${groupId}`);
      const data = await response.json();
      
      if (data.success) {
        return data;
      }
    } catch (error) {
      console.error('Failed to get group info:', error);
    }
    return null;
  };

  // Get all groups
  const getAllGroups = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/groups`);
      const data = await response.json();
      
      if (data.success) {
        return data.groups;
      }
    } catch (error) {
      console.error('Failed to get groups:', error);
    }
    return [];
  };

  // Get available groups with spot information
  const getAvailableGroups = async () => {
    try {
      const groups = await getAllGroups();
      return groups
        .filter(group => hasGroupAvailableSpots(group))
        .map(group => ({
          ...group,
          availableSpots: group.max_players - group.current_players
        }))
        .sort((a, b) => new Date(b.created_at) - new Date(a.created_at)); // Newest first
    } catch (error) {
      console.error('Failed to get available groups:', error);
      return [];
    }
  };

  // Check if player can join a specific group
  const canJoinGroup = (targetGroupId) => {
    // Can't join if already in any group
    if (groupId) {
      return { canJoin: false, reason: 'Already in a group' };
    }
    
    // Can't join if already in this specific group
    if (groupId === targetGroupId) {
      return { canJoin: false, reason: 'Already in this group' };
    }
    
    return { canJoin: true, reason: 'Can join' };
  };

  // Refresh group info and update parent
  const refreshGroupInfo = async () => {
    if (!groupId) return;
    
    const groupData = await getGroupInfo();
    if (groupData && onGroupUpdate) {
      onGroupUpdate({
        groupId: groupId,
        playerInfo: playerInfo,
        groupStatus: groupData.group.status
      });
    }
  };

  // Record game completion
  const recordGameCompletion = useCallback(async (won, finalTapCount, finalGameTime) => {
    console.log('checking session is record game completion', sessionId)
    if (!sessionId) return;
    console.log('loggin request', JSON.stringify({
      deviceId: deviceInfo.id,
      sessionId: sessionId,
      groupId: groupId,
      won: won,
      tapCount: finalTapCount,
      gameTime: finalGameTime
    }))
    try {
      const response = await fetch(`${API_BASE}/api/game/complete`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          deviceId: deviceInfo.id,
          sessionId: sessionId,
          groupId: groupId,
          won: won,
          tapCount: finalTapCount,
          gameTime: finalGameTime
        })
      });

      const data = await response.json();
      console.log('response', data)
      if (data.success) {
        console.log('Game completion recorded successfully');
      } else {
        console.error('Failed to record game completion:', data.error);
      }
    } catch (error) {
      console.error('Failed to record game completion:', error);
    }
  }, [sessionId, deviceInfo.id, groupId]);

  // Update game stats
  const updateGameStats = useCallback((newStats) => {
    setGameStats(prev => ({
      ...prev,
      ...newStats
    }));
  }, []);

  // Update player progress during game
  const updateProgress = useCallback(async (progress) => {
    if (!sessionId) return;
    
    try {
      await fetch(`${API_BASE}/api/game/session/${sessionId}/progress`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          progress: progress,
          groupId: groupId,
          deviceId: deviceInfo?.id
        })
      });
    } catch (error) {
      console.error('Failed to update progress:', error);
    }
  }, [sessionId, groupId, deviceInfo?.id]);

  // Handle game completion
  const handleGameComplete = useCallback((won, finalTapCount, finalGameTime) => {
    const endTime = Date.now();
    const gameTime = (endTime - gameStats.startTime) / 1000; // Convert to seconds
    
    setGameStats(prev => ({
      ...prev,
      endTime: endTime,
      tapCount: finalTapCount,
      gameTime: gameTime,
      won: won
    }));

    // Record completion in backend
    recordGameCompletion(won, finalTapCount, gameTime);

    // Notify parent component
    onGameComplete({
      won,
      tapCount: finalTapCount,
      gameTime: gameTime,
      deviceInfo,
      groupId
    });
  }, [gameStats.startTime, recordGameCompletion, onGameComplete, deviceInfo, groupId]);

  // Expose methods to parent component
  useImperativeHandle(ref, () => ({
    updateGameStats,
    updateProgress,
    handleGameComplete,
    joinGroup,
    createNewGroup,
    createGroupAndJoin,
    handleQRCodeScan,
    getGroupInfo,
    getAllGroups,
    getAvailableGroups,
    refreshGroupInfo,
    autoJoinLatestGroup,
    isInGroup,
    leaveGroup,
    resetSession,
    isDeviceProcessing,
    isActiveInstance,
    getGlobalStatus,
    clearGlobalState,
    getCurrentGroupStatus,
    isCurrentGroupFull,
    hasGroupAvailableSpots,
    getCurrentGroupAvailableSpots,
    canJoinGroup,
    getCurrentGroupInfo,
    groupId,
    playerInfo,
    groupStatus
  }));

  return null; // This component doesn't render anything
});

export default GameSessionManager;
